import countries, { type Alpha2Code } from 'i18n-iso-countries'
import arLocale from 'i18n-iso-countries/langs/ar.json'
import enLocale from 'i18n-iso-countries/langs/en.json'
import { type Nationality } from '../model/patient'

countries.registerLocale(arLocale)
countries.registerLocale(enLocale)

const alpha2Codes = new Set(Object.keys(countries.getAlpha2Codes()))

const legacyNationalities: Record<string, Alpha2Code> = {
  austrian: 'AT',
  british: 'GB',
  eg: 'EG',
  egypt: 'EG',
  egyptian: 'EG',
  egyt: 'EG',
  مصري: 'EG'
}

export interface CountryOption {
  label: string
  value: Alpha2Code
}

export function isCountryCode(value: unknown): value is Alpha2Code {
  return (
    typeof value === 'string' &&
    value === value.toUpperCase() &&
    alpha2Codes.has(value)
  )
}

export function normalizeNationality(
  value: unknown,
  emptyValue: Nationality = null
): Nationality {
  if (value === null) return null
  if (value === undefined) return emptyValue
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return emptyValue

  const upperValue = trimmed.toUpperCase()
  if (isCountryCode(upperValue)) return upperValue

  const legacyValue = legacyNationalities[trimmed.toLocaleLowerCase()]
  if (legacyValue) return legacyValue

  const countryCode =
    countries.getAlpha2Code(trimmed, 'en') ??
    countries.getAlpha2Code(trimmed, 'ar')

  return isCountryCode(countryCode)
    ? (countryCode.toUpperCase() as Alpha2Code)
    : null
}

export function getCountryOptions(locale: string): CountryOption[] {
  const language = locale.toLocaleLowerCase().startsWith('ar') ? 'ar' : 'en'
  const collator = new Intl.Collator(language)

  return Object.entries(countries.getNames(language, { select: 'official' }))
    .map(([value, label]) => ({
      label,
      value: value as Alpha2Code
    }))
    .sort((left, right) => collator.compare(left.label, right.label))
}
